import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Button, Checkbox, Form, Input, Row, Col, Result } from "antd";
import { HONEYPOT_FIELD, Honeypot, submitContact } from "../../utils/publicContact";

// Form item name -> field name expected by POST /public/contact.
const API_FIELDS = {
  username: "firstName",
  password: "lastName",
  email: "email",
  phone: "phone",
  message: "message",
  consent: "consent"
};

const GetStarted = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [honeypot, setHoneypot] = useState("");
  const [status, setStatus] = useState({ loading: false, error: "", success: false });
  const successTimer = useRef(null);
  const requiredRule = [{ required: true, message: t("key-company-contact.extra-info") }];

  useEffect(() => () => clearTimeout(successTimer.current), []);

  const handleFinish = async values => {
    setStatus({ loading: true, error: "", success: false });
    const payload = { site: "keys", formType: "contact", [HONEYPOT_FIELD]: honeypot };
    Object.entries(API_FIELDS).forEach(([item, api]) => {
      const value = values[item];
      payload[api] = typeof value === "string" ? value.trim() : value;
    });

    try {
      await submitContact(payload);
      form.resetFields();
      setStatus({ loading: false, error: "", success: true });
      successTimer.current = setTimeout(
        () => setStatus(prev => ({ ...prev, success: false })),
        5000
      );
    } catch (err) {
      const fields = Object.entries(API_FIELDS)
        .filter(([, api]) => err.fieldErrors?.[api])
        .map(([item, api]) => ({ name: item, errors: [err.fieldErrors[api]] }));
      if (fields.length) {
        form.setFields(fields);
        form.scrollToField(fields[0].name, { block: "center", focus: true });
      }
      let error = t("key-company-contact.error-send");
      if (fields.length) error = t("key-company-contact.error-fields");
      else if (err.status === 429) error = t("key-company-contact.error-too-many");
      setStatus({ loading: false, error, success: false });
    }
  };

  return (
    <section id="get-started" className="get-started section">
      <div className="container">
        <div className="section-title" data-aos="fade-up">
          <h2>{t("key-company-contact.title")}</h2>
        </div>

        <ul className="contact-details">
          <li>
            Telefon:{" "}
            <a href={`tel:${t("key-company-contact.telefon").replace(/[^\d+]/g, "")}`}>
              {t("key-company-contact.telefon")}
            </a>
          </li>
          <li>
            E-Mail:{" "}
            <a href={`mailto:${t("key-company-contact.E-mail")}`}>
              {t("key-company-contact.E-mail")}
            </a>
          </li>
          <li>Adresse: {t("key-company-contact.Address")}</li>
        </ul>

        <Row gutter={[24, 24]}>
          <Col xs={24} md={12}>
            <Result
              className="map-consent"
              title={t("key-company-contact.map.title")}
              subTitle={t("key-company-contact.map.item")}
              extra={
                <Button type="primary" key="console">
                  {t("key-company-contact.map.button-title")}
                </Button>
              }
            />
          </Col>
          <Col xs={24} md={12}>
            <Form
              className="contact-form"
              layout="vertical"
              name="basic"
              form={form}
              onFinish={handleFinish}
              requiredMark
            >
              <Honeypot value={honeypot} onChange={e => setHoneypot(e.target.value)} />

              <Form.Item
                label={t("key-company-contact.username")}
                name="username"
                rules={requiredRule}
              >
                <Input autoComplete="given-name" maxLength={80} />
              </Form.Item>

              <Form.Item
                label={t("key-company-contact.password")}
                name="password"
                rules={requiredRule}
              >
                <Input autoComplete="family-name" maxLength={80} />
              </Form.Item>

              <Form.Item
                label={t("key-company-contact.email-label")}
                name="email"
                rules={[
                  ...requiredRule,
                  { type: "email", message: t("key-company-contact.email-invalid") }
                ]}
              >
                <Input type="email" inputMode="email" autoComplete="email" maxLength={254} />
              </Form.Item>

              <Form.Item label={t("key-company-contact.phone-label")} name="phone">
                <Input type="tel" inputMode="tel" autoComplete="tel" maxLength={40} />
              </Form.Item>

              <Form.Item
                label={t("key-company-contact.message-label")}
                name="message"
                rules={requiredRule}
              >
                <Input.TextArea rows={5} maxLength={5000} />
              </Form.Item>

              <Form.Item
                name="consent"
                valuePropName="checked"
                label={null}
                rules={[
                  {
                    validator: (_, value) =>
                      value
                        ? Promise.resolve()
                        : Promise.reject(new Error(t("key-company-contact.consent-required")))
                  }
                ]}
              >
                <Checkbox>{t("key-company-contact.checkbox-title")}</Checkbox>
              </Form.Item>

              <p>{t("key-company-contact.extra-info")}</p>

              <div aria-live="polite">
                {status.success && (
                  <Alert
                    className="contact-form__status"
                    type="success"
                    showIcon
                    message={t("key-company-contact.success")}
                  />
                )}
              </div>
              {status.error && (
                <Alert
                  className="contact-form__status"
                  type="error"
                  showIcon
                  role="alert"
                  message={status.error}
                />
              )}

              <Form.Item label={null} className="contact-form__submit">
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={status.loading}
                  disabled={status.loading}
                >
                  {t("key-company-contact.send-buttontitle")}
                </Button>
              </Form.Item>
            </Form>
          </Col>
        </Row>
      </div>
    </section>
  );
};

export default GetStarted;
